import type { Profile, User } from "@amazing-care/types";
import { staffRoleSchema } from "@amazing-care/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { AuthAdapter, SessionTokens } from "../types";

const OAUTH_CALLBACK_PATH = "/auth/callback";

interface SupabaseAdapterOptions {
  /**
   * Email domain the Entra SAML connection is registered under in Supabase
   * (`supabase sso add --domains`). Required for Microsoft sign-in.
   */
  ssoDomain?: string;
}

function mapUser(supabaseUser: { id: string; email?: string; created_at: string }): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email ?? "",
    account_id: null,
    created_at: supabaseUser.created_at,
  };
}

function callbackUrl(next?: string): string {
  const url = new URL(OAUTH_CALLBACK_PATH, window.location.origin);
  if (next) url.searchParams.set("next", next);
  return url.toString();
}

/** supabase-js keeps the session under `sb-<project>-auth-token`. */
const SESSION_KEY = /^sb-.+-auth-token$/;

/**
 * The Teams sign-in popup keeps its session in sessionStorage (see the client
 * in @amazing-care/supabase). localStorage holds the sessions of the portal's
 * other tabs in this browser and is left alone.
 */
function forgetStoredSessions(): void {
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (SESSION_KEY.test(key)) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Storage blocked: nothing was kept to forget.
  }
}

export function createSupabaseAdapter(
  supabase: SupabaseClient,
  options: SupabaseAdapterOptions = {}
): AuthAdapter {
  return {
    async getUser() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user ? mapUser(user) : null;
    },

    async getProfile(userId: string): Promise<Profile | null> {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, email, full_name, role, sso_department_ids, manager_departments(department_id)"
        )
        .eq("id", userId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      // No row means a signed-in account that is not a member. RLS also hides
      // everything else from it; the UI shows a no-access screen.
      if (!data) return null;

      // The database stores lowercase enum values; the UI works in title case.
      const role = staffRoleSchema.parse(
        String(data.role).charAt(0).toUpperCase() + String(data.role).slice(1)
      );
      const departments = (data.manager_departments ?? []) as { department_id: string }[];

      return {
        id: data.id,
        email: data.email ?? "",
        full_name: data.full_name ?? "",
        role,
        // Portal grants plus the departments the member's Entra groups grant.
        managedDepartmentIds: [
          ...new Set([
            ...departments.map((row) => row.department_id),
            ...((data.sso_department_ids as string[] | null) ?? []),
          ]),
        ],
      };
    },

    async signInWithEmail(email: string, password: string) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
      return mapUser(data.user);
    },

    async signInWithMicrosoft({ next } = {}) {
      if (!options.ssoDomain) {
        throw new Error("Microsoft sign-in is not configured: VITE_SSO_DOMAIN is not set.");
      }
      // supabase-js navigates to Entra itself once it has the SAML request URL.
      const { error } = await supabase.auth.signInWithSSO({
        domain: options.ssoDomain,
        options: { redirectTo: callbackUrl(next) },
      });
      if (error) throw new Error(error.message);
    },

    async signOut() {
      const { error } = await supabase.auth.signOut();
      if (error) throw new Error(error.message);
    },

    async resetPassword(email: string) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${OAUTH_CALLBACK_PATH}?next=/reset-password`,
      });
      if (error) throw new Error(error.message);
    },

    async handOffSession(): Promise<SessionTokens | null> {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return null;
      // Refresh tokens rotate, and Supabase revokes a whole session when a
      // used one comes back. Two holders of one session (this window and the
      // receiver) would sooner or later sign each other out, so this window
      // forgets its copy, without revoking it, and the receiver keeps it.
      await supabase.auth.stopAutoRefresh();
      forgetStoredSessions();
      return { access_token: session.access_token, refresh_token: session.refresh_token };
    },

    async setSession(tokens: SessionTokens) {
      const { data, error } = await supabase.auth.setSession(tokens);
      if (error) throw new Error(error.message);
      if (!data.user) throw new Error("The sign-in did not return a user.");
      return mapUser(data.user);
    },

    onAuthStateChange(cb: (user: User | null) => void) {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        cb(session?.user ? mapUser(session.user) : null);
      });
      return () => subscription.unsubscribe();
    },
  };
}
